import { useState } from "react";
import {
    Checkbox,
    Divider,
    Dropdown,
    DropdownOption,
    RadioButton,
    TextField,
    Tooltip,
    Typography,
} from "@volvo/vcdk-react";
import type { RadioButtonChangeEvent } from "@volvo/vcdk/";
import { UITEXT, SME_OPTIONS, EU_COUNTRIES } from "../../../../constants/const";
import FileUpload from "../../FileUpload/FileUpload";
import NavigationButtons from "../NavigationButtons/NavigationButtons";
import type { ThirdPartyDataRecipientInformationTabProps } from "../types";
import styles from "./ThirdPartyDataRecipientInformationTab.module.css";

const ThirdPartyDataRecipientInformationTab: React.FC<
    ThirdPartyDataRecipientInformationTabProps
> = ({
    formData,
    handleTextChange,
    handleCheckboxChange,
    handleChoiceChange,
    handleCountryChange,
    handleFileUpload,
    onNext,
    onBack,
    errors = {},
    uploadingFiles,
}) => {
    const [
        thirdPartyDataRecipientInformationTooltip,
        setThirdPartyDataRecipientInformationTooltip,
    ] = useState<HTMLElement | null>(null);
    const [thirdPartyUploadToolTip, setThirdPartyUploadToolTip] =
        useState<HTMLElement | null>(null);
    const [countryTooltip, setCountryTooltip] =
        useState<HTMLElement | null>(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onCountryChange = (selected: any) => {
        const candidate = selected?.detail?.value ?? selected?.target?.value;
        const newValue =
            typeof candidate === "string" && candidate.trim() !== ""
                ? candidate
                : "";
        handleCountryChange(newValue);
    };

    return (
        <div className={styles.Section}>
            <Typography variant="caption1">
                {UITEXT.ThirdPartyDataRecipientInformation.Notice}
            </Typography>
            <Divider />
            <div className={styles.Content}>
                <div className={styles.FieldWidth}>
                    <div className={styles.TextfieldWrapper}>
                        <div>
                            <TextField
                                name="thirdPartyDataRecipientName"
                                label="Requesting company name"
                                placeholder="Enter requesting company name"
                                width="100%"
                                value={formData.thirdPartyDataRecipientName}
                                onChange={handleTextChange}
                            />
                        </div>
                        <div className={styles.ErrorWrapper}>
                            {errors && errors.thirdPartyDataRecipientName && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.thirdPartyDataRecipientName}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={styles.TextfieldWrapper}>
                        <div>
                            <Dropdown
                                label="Requesting company country of registration"
                                onChange={onCountryChange}
                                multi={false}
                                size="medium"
                                floatingLabel={false}
                                placeholder="Select country"
                                clearable
                                filterInput={true}
                                value={formData.thirdPartyDataRecipientCountryOfRegistration}
                                ref={(ref) => setCountryTooltip(ref)}
                            >
                                {EU_COUNTRIES.map((country) => (
                                    <DropdownOption key={country} value={country}>
                                        {country}
                                    </DropdownOption>
                                ))}
                            </Dropdown>
                            <Tooltip
                                reference={countryTooltip}
                                offset={20}
                                id="tooltip-country"
                                variant="default"
                                placement="right"
                            >
                                Select the requesting company country of registration
                            </Tooltip>
                        </div>
                        <div className={styles.ErrorWrapper}>
                            {errors && errors.thirdPartyDataRecipientCountryOfRegistration && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.thirdPartyDataRecipientCountryOfRegistration}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
                <Divider vertical />
                <div className={styles.FieldWidth}>
                    <div>
                        <FileUpload
                            label="Upload requesting company certificate of registration"
                            onFileSelected={(file) => {
                                handleFileUpload(
                                    "thirdPartyDataRecipientCertificateOfRegistrationAttachment",
                                    URL.createObjectURL(file),
                                    file
                                );
                                handleFileUpload(
                                    "thirdPartyDataRecipientCertificateOfRegistrationAttachmentName",
                                    file.name,
                                    file
                                );
                            }}
                            onFileClear={() => {
                                handleFileUpload(
                                    "thirdPartyDataRecipientCertificateOfRegistrationAttachment",
                                    ""
                                );
                                handleFileUpload(
                                    "thirdPartyDataRecipientCertificateOfRegistrationAttachmentName",
                                    ""
                                );
                            }}
                            externalFileName={
                                formData.thirdPartyDataRecipientCertificateOfRegistrationAttachmentName
                            }
                            isUploading={uploadingFiles.has(
                                "thirdPartyDataRecipientCertificateOfRegistrationAttachment"
                            )}
                            ref={(ref) => setThirdPartyUploadToolTip(ref)}
                        />
                        <Tooltip
                            reference={thirdPartyUploadToolTip}
                            offset={20}
                            id="tooltip-1"
                            variant="default"
                        >
                            Upload an extract from the national business
                            register of the country where the company is
                            incorporated
                        </Tooltip>
                    </div>
                    <div className={styles.ErrorWrapper}>
                        {errors &&
                            errors.thirdPartyDataRecipientCertificateOfRegistrationAttachment && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {
                                        errors.thirdPartyDataRecipientCertificateOfRegistrationAttachment
                                    }
                                </Typography>
                            )}
                    </div>
                </div>
            </div>
            <Divider />
            <div className={styles.RequesterInformationTextSection}>
                <div>
                    <Typography variant="caption1">
                        {UITEXT.ThirdPartyDataRecipientInformation.SME}
                    </Typography>
                    <br />
                    <div className={styles.SMERadioButton}>
                        {SME_OPTIONS.map((opt, i) => (
                            <RadioButton
                                key={i}
                                error={opt.error}
                                label={opt.label}
                                value={opt.value}
                                name="thirdPartyDataRecipientSME"
                                size="small"
                                onChange={(e: RadioButtonChangeEvent) =>
                                    handleChoiceChange(
                                        typeof e.detail.value === "boolean"
                                            ? e.detail.value
                                            : String(
                                                  e.detail.value
                                              ).toLowerCase() === "true"
                                    )
                                }
                                checked={
                                    formData.thirdPartyDataRecipientSME ===
                                    opt.value
                                }
                            />
                        ))}
                    </div>
                    <Typography variant="caption2">
                        {
                            UITEXT.ThirdPartyDataRecipientInformation
                                .SME_Explanation
                        }
                    </Typography>
                    {formData.thirdPartyDataRecipientSME === true && (
                        <div className={styles.SMEConfirmationWrapper}>
                            <div className={styles.SMEConfirmationCheckbox}>
                                <Checkbox
                                    name="thirdPartyDataRecipientSMEConfirmation"
                                    className={styles.Checkbox}
                                    size="small"
                                    label={
                                        UITEXT.ThirdPartyDataRecipientInformation
                                            .SME_Confirmation
                                    }
                                    checked={
                                        formData.thirdPartyDataRecipientSMEConfirmation
                                    }
                                    onChange={handleCheckboxChange}
                                />
                            </div>
                            {errors &&
                                errors.thirdPartyDataRecipientSMEConfirmation && (
                                    <div className={styles.ErrorWrapper}>
                                        <Typography
                                            variant="caption2"
                                            className={styles.ErrorMessage}
                                        >
                                            {
                                                errors.thirdPartyDataRecipientSMEConfirmation
                                            }
                                        </Typography>
                                    </div>
                                )}
                        </div>
                    )}
                </div>
                <Divider />
                <Checkbox
                    name="thirdPartyDataRecipientGatekeeper"
                    className={styles.Checkbox}
                    size="small"
                    label={UITEXT.ThirdPartyDataRecipientInformation.Gatekeeper}
                    checked={formData.thirdPartyDataRecipientGatekeeper}
                    onChange={handleCheckboxChange}
                    ref={(ref) =>
                        setThirdPartyDataRecipientInformationTooltip(ref)
                    }
                />
                <Tooltip
                    reference={thirdPartyDataRecipientInformationTooltip}
                    offset={20}
                    id="tooltip-1"
                    variant="default"
                >
                    The list of the designated gatekeepers is published and
                    regularly updated by the European Commission
                </Tooltip>
                <div>
                    <div className={styles.ErrorWrapper}>
                        {errors && errors.thirdPartyDataRecipientGatekeeper && (
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {errors.thirdPartyDataRecipientGatekeeper}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
            <NavigationButtons onNext={onNext} onBack={onBack} />
        </div>
    );
};

export default ThirdPartyDataRecipientInformationTab;
